import Highcharts from "highcharts";
import HighchartsExport from "highcharts/modules/exporting";
import HighchartsReact from "highcharts-react-official";
import { TranslationService } from "../../../services/TranslationService";
import {
  formatCurrencyPortal,
  formatInteger
} from "../../../utils/FormatUtils";
import CompanyService from "../../../services/CompanyService";
import ErrorCatcher from "../../shared/ErrorCatcher";
import { useState } from "react";

HighchartsExport(Highcharts);

Highcharts.setOptions({
  lang: {
    thousandsSep: window.location.href
      .replace(window.location.origin, "")
      .startsWith("/en/")
      ? ","
      : ".",
    decimalPoint: window.location.href
      .replace(window.location.origin, "")
      .startsWith("/en/")
      ? "."
      : ","
  }
});

interface Props {
  chartData: Array<Array<boolean | number | string>>;
  currencySymbol: string;
}

const ConsolidatedDistributionChart: React.FC<Props> = ({
  chartData,
  currencySymbol
}) => {
  const [error, setError] = useState(false);

  if (error || !chartData || chartData.length < 3) {
    return <></>;
  }

  const rawCategories = (chartData[0] || []) as string[];
  const amounts = (chartData[1] || []) as number[];
  const overdueFlags = (chartData[2] || []) as boolean[];
  const showCents = CompanyService.getCompanyAuth()?.ShowCents ?? false;

  const { translate } = TranslationService;

  const categories = rawCategories.map(translateAgingCategory);

  const noDueData = amounts.map((amount, index) =>
    overdueFlags[index] ? Number(amount) : null
  );
  const dueData = amounts.map((amount, index) =>
    !overdueFlags[index] ? Number(amount) : null
  );

  const chartOptions: Highcharts.Options = {
    exporting: {
      buttons: {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            "printChart",
            "separator",
            "downloadPNG",
            "downloadPDF"
          ]
        }
      }
    },
    credits: { enabled: false },
    title: {
      text: translate.ReceivablesAging
    },
    chart: {
      type: "column",
      height: 400
    },
    legend: {
      enabled: true
    },
    xAxis: {
      categories,
      labels: {
        style: { fontSize: "10px" },
        rotation: -45,
        align: "right"
      }
    },
    yAxis: {
      title: { text: "" },
      gridLineColor: "#e3e3e3",
      gridLineWidth: 1,
      labels: {
        formatter: function () {
          const value = parseFloat(this.value.toString());
          const abs = Math.abs(value);
          if (abs >= 1_000_000_000_000) {
            return (
              (value / 1_000_000_000_000).toFixed(1).replace(/\.0$/, "") + "T"
            );
          }
          if (abs >= 1_000_000_000) {
            return (value / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "T";
          }
          if (abs >= 1_000_000) {
            return (value / 1_000_000).toFixed(1).replace(/\.0$/, "") + "T";
          }
          if (abs >= 1_000) {
            return (value / 1_000).toFixed(1).replace(/\.0$/, "") + "T";
          }
          return formatInteger(value);
        }
      }
    },
    tooltip: {
      formatter: function () {
        return `<b>${this.x}</b><br/>${formatCurrencyPortal(this.y ?? 0, currencySymbol, showCents)}`;
      }
    },
    plotOptions: {
      column: {
        pointWidth: 36,
        borderRadius: 4,
        dataLabels: { enabled: false },
        grouping: false
      }
    },
    series: [
      {
        type: "column",
        name: translate.NoDue,
        color: "#0076B6",
        data: noDueData
      },
      {
        type: "column",
        name: translate.Due,
        color: "#EF4E38",
        data: dueData
      }
    ]
  };

  return (
    <div className="card">
      <div className="card-body">
        <ErrorCatcher onError={() => setError(true)}>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </ErrorCatcher>
      </div>
    </div>
  );
};

function translateAgingCategory(category: string): string {
  const { getTranslation } = TranslationService;

  // "Last {N} days"
  let m = category.match(/^Last (\d+) days?$/i);
  if (m) return getTranslation("Aging-LastXDays").replace("{0}", m[1]);

  // "More than {N} days ago"
  m = category.match(/^More than (\d+) days? ago$/i);
  if (m) return getTranslation("Aging-MoreXDaysAgo").replace("{0}", m[1]);

  // "Less than {N} days ago"
  m = category.match(/^Less than (\d+) days? ago$/i);
  if (m) return getTranslation("Aging-LessXDaysAgo").replace("{0}", m[1]);

  // "{N} - {M} days ago"
  m = category.match(/^(\d+)\s*-\s*(\d+) days? ago$/i);
  if (m)
    return getTranslation("Aging-MoreXYDays")
      .replace("{0}", m[1])
      .replace("{1}", m[2]);

  // "In {N} days or more"
  m = category.match(/^In (\d+) days? or more$/i);
  if (m) return getTranslation("Aging-MoreXDays").replace("{0}", m[1]);

  // "Next {N} days"
  m = category.match(/^Next (\d+) days?$/i);
  if (m) return getTranslation("Aging-LessXDays").replace("{0}", m[1]);

  // "In {N} - {M} days"
  m = category.match(/^In (\d+)\s*-\s*(\d+) days?$/i);
  if (m)
    return getTranslation("Aging-InXYDays")
      .replace("{0}", m[1])
      .replace("{1}", m[2]);

  return category;
}

export default ConsolidatedDistributionChart;
